import { Amount } from '@ui/components/Amount/Amount';
import { AmountUsd } from '@ui/components/Amount/AmountUsd';
import { X } from '@ui/components/icons';
import { LongShortBadge } from '@ui/components/LongShortBadge';
import { StatLabel } from '@ui/components/Stats';
import {
  calculateSpreadPercentage,
  useSpreadPercentage,
} from '@ui/features/Spread/useSpreadPercentage';
import { SlValidationResult } from '@ui/features/Trade/utils/validateStoppLossPrice';
import { TpValidationResult } from '@ui/features/Trade/utils/validateTakeProfitPrice';
import { RequireConnectionClickable } from '@ui/features/Wallet/components/RequireConnectionClickable';
import { usePriceLive } from '@ui/hooks/price/usePriceLive';
import { useTradePairInfosByPairBase } from '@ui/hooks/trade/useTradePairInfos';
import { useNativeToken } from '@ui/hooks/useNativeToken';
import { useTokenDisplayDecimals } from '@ui/hooks/useTokenDisplayDecimals';
import { ZERO_BIG_NUMBER } from '@ui/lib/bignumber';
import { Portal } from '@ui/lib/park/portal';
import { Button, ButtonProps } from '@ui/lib/park/ui/button';
import { Dialog } from '@ui/lib/park/ui/dialog';
import { Icon } from '@ui/lib/park/ui/icon';
import { Text } from '@ui/lib/park/ui/text';
import {
  getActiveSlOrders,
  getActiveTpOrders,
  getDecreaseOrderTriggerPrice,
} from '@ui/services/contract/hooks/TradingPortalFacet/useDecreaseOrders/selectors';
import type { DecreaseOrderRow } from '@ui/services/contract/hooks/TradingPortalFacet/useDecreaseOrders/types';
import BigNumber from 'bignumber.js';
import { isNil } from 'lodash-es';
import { useTranslation } from 'next-i18next';
import { useCallback, useState } from 'react';
import {
  Box,
  Grid,
  HStack,
  Stack,
  styled,
  VStack,
} from 'styled-system/jsx';

import { useGetTargetMaxTpRatios } from '../../hooks/useGetTargetMaxTpRatios';
import { EditTpSlType, GenericTradeParams, TpSlValueState } from '../../types';
import { TradeLeverageBadge } from '../LeverageBadge';
import { SizeUsd } from '../SizeUsd';
import { usePositionSize } from '../TradeModule/hooks/usePositionSize';
import { formatLeverage } from '../TradeModule/utils/formatLeverage';
import { StopLossChange, TakeProfitChange } from './components/changes';
import { EditTpSl } from './components/EditTpSl';
import { EntryPrice } from './components/EntryPrice';
import { LiqPrice } from './components/LiqPrice';
import { MarketPrice } from './components/MarketPrice';
import {
  EditTpSlBaseline,
  getEditTpSlBaseline,
} from './hooks/getEditTpSlBaseline';
import { useEditTpSlConfirmBtnDisabled } from './hooks/useEditTpSlConfirmBtnDisabled';
import {
  useEditSlValidation,
  useEditTpValidation,
} from './hooks/useEditTpSlValidation';
import { useEstOrderUpdateTpSlGasFee } from './hooks/useEstOrderUpdateTpSlGasFee';
import { useEstTradeUpdateTpSlGasFee } from './hooks/useEstTradeUpdateTpSlGasFee';
import { useSyncTpSl } from './hooks/useSyncTpSl';
import { useUpdateOrderTpAndSl } from './hooks/useUpdateOrderTpAndSl';
import { useUpdateTradeTpAndSl } from './hooks/useUpdateTradeTpAndSl';
import { valueStyle } from './styles';

export const EditTpSlOverlay = ({
  children,
  genericTradeParams,
  activeDecreaseOrders,
  collateralMarketPrice,
  pairMarketPrice,
  type = EditTpSlType.POSITION,
  ...rest
}: Omit<Dialog.RootProps, 'type'> & {
  pairMarketPrice: BigNumber;
  collateralMarketPrice: BigNumber;
  type: EditTpSlType;
  activeDecreaseOrders?: DecreaseOrderRow[];
} & GenericTradeParams) => {
  const { t } = useTranslation();

  const [isOpen, setOpen] = useState(false);

  const [tp, setTp] = useState<TpSlValueState>(() => ({
    priceValue: '',
    pnlPercentage: undefined,
    deltaPnl: undefined,
    isLong: false,
    isPnlPercentage: false,
  }));

  const [sl, setSl] = useState<TpSlValueState>(() => ({
    priceValue: '',
    pnlPercentage: undefined,
    deltaPnl: undefined,
    isLong: false,
    isPnlPercentage: false,
  }));

  const priceDisplayDecimals = useTokenDisplayDecimals(
    genericTradeParams?.pair.baseSymbol,
    genericTradeParams?.pair.priceDisplayDecimals,
  );

  const [baseline, setBaseline] = useState<EditTpSlBaseline | null>(null);

  const onSuccessCallBack = () => {
    setOpen(false);
    setBaseline(null);
  };

  const {
    writeContract: updateTradeTpSl,
    isPending: isUpdateTradeTpSlPending,
  } = useUpdateTradeTpAndSl({
    genericTradeParams,
    activeDecreaseOrders,
    tp,
    sl,
    onSuccess: onSuccessCallBack,
  });

  const {
    writeContract: updateOrderTpSl,
    isPending: isUpdateOrderTpSlPending,
  } = useUpdateOrderTpAndSl({
    genericTradeParams,
    tp,
    sl,
    onSuccess: onSuccessCallBack,
  });

  const isPositionType = type === EditTpSlType.POSITION;

  const onConfirm = () => {
    isPositionType ? updateTradeTpSl() : updateOrderTpSl();
  };

  const syncTpSl = useSyncTpSl({
    genericTradeParams,
    activeDecreaseOrders,
    setSl,
    setTp,
  });

  const resetModalForm = useCallback(() => {
    syncTpSl({
      _collateralTokenMarketPrice: collateralMarketPrice,
      _pairMarketPrice: pairMarketPrice,
    });
    setBaseline(
      getEditTpSlBaseline({
        takeProfit:
          getDecreaseOrderTriggerPrice(
            getActiveTpOrders(activeDecreaseOrders)[0],
          ) ?? genericTradeParams.takeProfit,
        stopLoss:
          getDecreaseOrderTriggerPrice(
            getActiveSlOrders(activeDecreaseOrders)[0],
          ) ?? genericTradeParams.stopLoss,
        priceDisplayDecimals,
      }),
    );
  }, [
    activeDecreaseOrders,
    collateralMarketPrice,
    genericTradeParams.stopLoss,
    genericTradeParams.takeProfit,
    pairMarketPrice,
    priceDisplayDecimals,
    syncTpSl,
  ]);

  const leverageValue = genericTradeParams
    .getLeverage({
      collateralTokenMarketPrice: collateralMarketPrice ?? ZERO_BIG_NUMBER,
    })
    .toNumber();

  const collateralUsdBeforeOpenPositionFees =
    genericTradeParams.getCollateralUsd(
      collateralMarketPrice ?? ZERO_BIG_NUMBER,
    );

  const positionPair = useTradePairInfosByPairBase(
    genericTradeParams.pair.address,
  );

  const maxTpRatios = useGetTargetMaxTpRatios(
    genericTradeParams.pair.address,
    leverageValue,
  );

  const tpslPositionSize = usePositionSize({
    inputUsd: collateralUsdBeforeOpenPositionFees,
    isBeforeFee: true,
    _leverage: leverageValue,
  });

  const livePairPrice = usePriceLive(genericTradeParams.pair.baseSymbol);

  const spreadPercentageInfo = useSpreadPercentage({
    tradePair: positionPair,
    positionSize: tpslPositionSize,
    pairPrice: isPositionType ? livePairPrice : genericTradeParams.entryPrice,
  });

  const spreadPercentage = calculateSpreadPercentage({
    isLong: genericTradeParams.isLong,
    spreadPercentageInfo,
    isClosePrice: true,
  });

  const tpValidation = useEditTpValidation({
    genericTradeParams,
    tp,
    maxTpRatios,
    spreadPercentage,
    leverage: leverageValue,
  });

  const slValidation = useEditSlValidation({
    genericTradeParams,
    sl,
  });

  const isBtnDisabled = useEditTpSlConfirmBtnDisabled({
    baseline,
    tp,
    sl,
    tpValidation,
    slValidation,
  });

  const leverage =
    isNil(collateralMarketPrice) || isNil(pairMarketPrice)
      ? undefined
      : formatLeverage(leverageValue);

  return (
    <Dialog.Root
      lazyMount
      unmountOnExit
      open={isOpen}
      onOpenChange={(v) => {
        setOpen(v.open);
        if (v.open) {
          resetModalForm();
          return;
        }

        setBaseline(null);
      }}
      {...rest}
    >
      <Dialog.Trigger asChild>{children}</Dialog.Trigger>
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content
            w="400px"
            maxW="calc(100vw - 32px)"
            data-cy="EditTpSlModalContent"
          >
            <Stack gap="3" p="4" pb={0}>
              <HStack p={0} justifyContent="space-between">
                <Dialog.Title p={0}>
                  {isPositionType
                    ? t('common.Edit Stop Loss / Take Profit')
                    : t('common.Edit Limit Order')}
                </Dialog.Title>
                <Dialog.CloseTrigger boxShadow="none" bg="none">
                  <Icon w="20px" h="20px">
                    <X />
                  </Icon>
                </Dialog.CloseTrigger>
              </HStack>
              <Dialog.Description>
                <VStack gap={0}>
                  <HStack
                    p="3"
                    borderRadius="4px"
                    border="1px solid"
                    borderColor="v2.border.default"
                    gap={1}
                    w="full"
                    bg="v2.bg.surface1"
                  >
                    <styled.img
                      src={genericTradeParams.pair.image}
                      boxSize="4"
                      borderRadius="2px"
                    />
                    <Text textStyle="headings.default">
                      {genericTradeParams.pair.baseName}
                    </Text>
                    <LongShortBadge isLong={genericTradeParams.isLong} />
                    <TradeLeverageBadge
                      genericTradeParams={genericTradeParams}
                    />
                  </HStack>
                  <Grid
                    my={2}
                    p="3"
                    borderRadius="4px"
                    border="1px solid"
                    borderColor="v2.border.default"
                    gap={1}
                    w="full"
                    bg="v2.bg.surface1"
                    gridTemplateColumns={
                      isPositionType ? '1fr 1fr 2fr' : '1fr 2fr'
                    }
                  >
                    <EntryPrice
                      genericTradeParams={genericTradeParams}
                      type={type}
                    />

                    {isPositionType && (
                      <MarketPrice genericTradeParams={genericTradeParams} />
                    )}

                    <LiqPrice genericTradeParams={genericTradeParams} />
                  </Grid>
                </VStack>
              </Dialog.Description>
            </Stack>
            <Stack gap={0} p="4" pt={0} borderTopWidth={0}>
              <Box>
                <EditTpSl
                  tp={tp}
                  setTp={setTp}
                  sl={sl}
                  setSl={setSl}
                  genericTradeParams={genericTradeParams}
                  isPositionType={isPositionType}
                  maxTpRatios={maxTpRatios}
                  spreadPercentage={spreadPercentage}
                  leverage={leverageValue}
                  collateralUsd={collateralUsdBeforeOpenPositionFees}
                  tpValidation={tpValidation}
                  slValidation={slValidation}
                />
              </Box>

              <Grid
                mt={2}
                p={3}
                rowGap={2}
                borderRadius={4}
                border="1px solid"
                borderColor="v2.border.default"
                bg="v2.bg.surface1"
                gridTemplateColumns="1fr auto"
              >
                <StatLabel>
                  {isPositionType
                    ? t('common.Close Size')
                    : t('common.Position Size')}
                </StatLabel>
                <SizeUsd
                  genericTradeParams={genericTradeParams}
                  css={valueStyle}
                  type={type}
                />

                {!isPositionType && (
                  <>
                    <StatLabel>{t('common.Trigger Price')}</StatLabel>
                    <AmountUsd
                      value={genericTradeParams.entryPrice}
                      css={valueStyle}
                      opts={{
                        displayDecimals: priceDisplayDecimals,
                      }}
                      data-cy="TriggerPrice"
                      data-cy-value={genericTradeParams.entryPrice}
                    />
                    <StatLabel>{t('common.Leverage')}</StatLabel>
                    <Text
                      css={valueStyle}
                      data-cy="leverage"
                      data-cy-value={leverage}
                    >
                      {leverage}x
                    </Text>
                  </>
                )}

                <StatLabel>{t('common.Take Profit at')}</StatLabel>
                <TakeProfitChange
                  genericTradeParams={genericTradeParams}
                  toTakeProfitPrice={
                    new BigNumber(tp.priceValue).isNaN()
                      ? genericTradeParams.takeProfit
                      : new BigNumber(tp.priceValue)
                  }
                />

                <StatLabel>{t('common.Stop Loss at')}</StatLabel>
                <StopLossChange
                  genericTradeParams={genericTradeParams}
                  toStopLossPrice={
                    new BigNumber(sl.priceValue).isNaN()
                      ? genericTradeParams.stopLoss
                      : new BigNumber(sl.priceValue)
                  }
                />
              </Grid>
            </Stack>
            <Stack flexDir="column" gap={3} p="4" pt={0} borderTop="none">
              <HStack
                p="2"
                justifyContent="space-between"
                borderTop="1px solid"
                borderTopColor="v2.border.hairline"
              >
                <StatLabel>{t('common.Estimated Gas Fee')}</StatLabel>
                <EstimatedGasFee
                  baseline={baseline}
                  genericTradeParams={genericTradeParams}
                  activeDecreaseOrders={activeDecreaseOrders}
                  tp={tp}
                  sl={sl}
                  type={type}
                  tpValidation={tpValidation}
                  slValidation={slValidation}
                />
              </HStack>
              {/* <WarningMsg
                warningMsg={t(
                  'common.Taking profit below 50% may not cover fees',
                )}
                color="primary.orangeLight"
                textStyle="text.xsmall"
                visibility={
                  !isBtnDisabled && tp.pnlPercentage && tp.pnlPercentage <= 0.5
                    ? 'visible'
                    : 'hidden'
                }
              /> */}
              <EditTpSlButton
                disabled={isBtnDisabled}
                onClick={onConfirm}
                loading={isUpdateTradeTpSlPending || isUpdateOrderTpSlPending}
              />
            </Stack>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
};

const EstimatedGasFee = ({
  baseline,
  genericTradeParams,
  activeDecreaseOrders,
  tp,
  sl,
  type,
  tpValidation,
  slValidation,
}: {
  baseline: EditTpSlBaseline | null;
  tp: TpSlValueState;
  sl: TpSlValueState;
  type: EditTpSlType;
  activeDecreaseOrders?: DecreaseOrderRow[];
  tpValidation: TpValidationResult | undefined;
  slValidation: SlValidationResult | undefined;
} & GenericTradeParams) => {
  const isPositionType = type === EditTpSlType.POSITION;
  const { data: estUpdateTradeTpSlGasFee } = useEstTradeUpdateTpSlGasFee({
    baseline,
    genericTradeParams,
    activeDecreaseOrders,
    tp,
    sl,
    tpValidation,
    slValidation,
    opts: {
      enabled: isPositionType,
    },
  });
  const { data: estUpdateOrderTpSlGasFee } = useEstOrderUpdateTpSlGasFee({
    baseline,
    genericTradeParams,
    tp,
    sl,
    tpValidation,
    slValidation,
    opts: {
      enabled: !isPositionType,
    },
  });
  const { symbol } = useNativeToken();

  const gasFee =
    type === EditTpSlType.POSITION
      ? estUpdateTradeTpSlGasFee
      : estUpdateOrderTpSlGasFee;

  return (
    <HStack gap={1}>
      <Amount
        css={valueStyle}
        value={gasFee?.gasInNativeToken}
        opts={{
          suffix: ' ' + symbol,
        }}
        data-cy="EstimatedGasFeeInNativeToken"
        data-cy-value={gasFee?.gasInNativeToken}
      />
      <AmountUsd
        value={gasFee?.gasInUsd}
        css={valueStyle}
        opts={{
          prefix: '($',
          suffix: ')',
        }}
        data-cy="EstimatedGasFeeInUsd"
        data-cy-value={gasFee?.gasInUsd}
      />
    </HStack>
  );
};

const EditTpSlButton = ({ ...props }: ButtonProps) => {
  const { t } = useTranslation();

  return (
    <RequireConnectionClickable>
      <Button width="full" variant="primary" size="big" {...props}>
        {t('common.Confirm')}
      </Button>
    </RequireConnectionClickable>
  );
};
